import { useRef } from "react";
import { content } from "../content";
import { motion, useScroll, useTransform } from "framer-motion";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Booking } from "../components/Booking";
import { Avatar } from "../components/Avatar";
import { DocCard } from "../components/DocCard";
import { SelfCheck } from "../components/SelfCheck";
import { Check, X, TrendingUp, Globe, Coins, Infinity as InfinityIcon } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

const EASE = [0.22, 1, 0.36, 1];

function HeroTitle({ text }) {
  const words = text.split(" ");
  return (
    <h1 className="hero-h1" data-testid="hero-title">
      {words.map((w, i) => (
        <span key={i} className="mask">
          <motion.span
            className="word"
            initial={{ y: "120%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 + i * 0.07, ease: EASE }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export function HomePage({ lang }) {
  const t = content[lang];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.3]);
  const fade = (d) => ({
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay: d, ease: EASE },
  });

  return (
    <div>
      <Seo title={t.meta.title} description={t.meta.description} lang={lang} />
      <Header lang={lang} t={t} />

      <section className="bg-navy">
        <div className="wrap sec-hero" ref={heroRef} data-testid="hero">
          <motion.div className="hero-stack" style={{ y, opacity: heroOpacity }}>
            <motion.p className="eyebrow hero-eyebrow" {...fade(0)}>{t.hero.eyebrow}</motion.p>
            <HeroTitle text={t.hero.title} />
            <motion.p className="hero-sub" {...fade(0.55)}>{t.hero.sub}</motion.p>
            <motion.div className="hero-ctas" {...fade(0.7)}>
              <a href="#contact" className="btn" data-testid="hero-cta-call">{t.hero.cta1}</a>
              <a href="#how" className="btn2" data-testid="hero-cta-how">{t.hero.cta2}</a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="bg-light">
        <div className="wrap sec-trust" data-testid="trust-strip">
          {t.trust.map((f, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="trust-item">
                <strong>{f.t}</strong>
                <span className="p">{f.d}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section>
        <div className="wrap sec">
          <div className="section-inner">
            <Reveal>
              <div className="section-head">
                <p className="eyebrow">{t.why.eyebrow}</p>
                <h2 className="h2">{t.why.h2}</h2>
                <p className="lead">{t.why.lead}</p>
              </div>
            </Reveal>
            <div className="grid-3" data-testid="why-cards">
              {t.why.cards.map((c, i) => {
                const Icon = [Globe, Coins, InfinityIcon][i];
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div className="why-item" data-testid={`why-card-${i + 1}`}>
                      <div className="why-icon"><Icon size={20} strokeWidth={2} /></div>
                      <h3 className="h3">{c.t}</h3>
                      <p className="p">{c.d}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="how">
        <div className="wrap sec">
          <div className="section-inner">
            <Reveal>
              <div className="section-head">
                <p className="eyebrow">{t.how.eyebrow}</p>
                <h2 className="h2">{t.how.h2}</h2>
              </div>
            </Reveal>
            <div className="grid-4" data-testid="how-steps">
              {t.how.steps.map((s, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="card step-card">
                    <div className="num">{i + 1}</div>
                    <h3 className="h3">{s.t}</h3>
                    <p className="p">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>



      <section id="sense" className="bg-light">
        <div className="wrap sec">
          <div className="section-inner">
            <Reveal>
              <div className="section-head">
                <p className="eyebrow">{t.sense.eyebrow}</p>
                <h2 className="h2">{t.sense.h2}</h2>
                <p className="lead">{t.sense.lead}</p>
              </div>
            </Reveal>
            <div className="grid-3" data-testid="sense-cards">
              {[
                { key: "fits", title: "fitsT", variant: "fit", Icon: Check },
                { key: "nots", title: "notT", variant: "not", Icon: X },
                { key: "look", title: "lookT", variant: "chances", Icon: TrendingUp },
              ].map(({ key, title, variant, Icon }, i) => (
                <Reveal key={key} delay={i * 0.08}>
                  <div className={`sense-card sense-${variant}`} data-testid={`sense-card-${variant}`}>
                    <div className="sense-badge"><Icon size={17} strokeWidth={2.6} /></div>
                    <h3 className="h3">{t.sense[title]}</h3>
                    <ul className="list">
                      {t.sense[key].map((li, j) => <li key={j}>{li}</li>)}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div className="note" data-testid="note-box">
                <strong>{t.sense.noteT}</strong>
                <span>{t.sense.note}</span>
              </div>
            </Reveal>
            <Reveal>
              <SelfCheck t={t.sense} />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-navy costs">
        <div className="wrap sec">
          <div className="section-inner">
            <Reveal>
              <div className="section-head">
                <p className="eyebrow">{t.costs.eyebrow}</p>
                <h2 className="h2">{t.costs.h2}</h2>
              </div>
            </Reveal>
            <div className="costs-grid" data-testid="costs">
              {t.costs.items.map((c, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="cost-item">
                    <div className="cost-value">{c.v}</div>
                    <p>{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <p className="costs-footnote">{t.costs.footnote}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section>
        <div className="wrap role">
          <Reveal className="role-left">
            <p className="eyebrow">{t.role.eyebrow}</p>
            <h2 className="h2">{t.role.h2}</h2>
            <p className="lead">{t.role.p1}</p>
            <p className="lead">{t.role.p2}</p>
          </Reveal>
          <Reveal className="role-right" delay={0.12}>
            <div className="profile">
              <Avatar />
              <div className="profile-info">
                <strong className="profile-name">{t.role.name}</strong>
                <span className="p">{t.role.role1}</span>
                <span className="p" style={{ fontSize: "0.9rem" }}>{t.role.role2}</span>
                {t.role.linkedin && (
                  <a href={t.role.linkedin} target="_blank" rel="noopener" className="linkedin-btn" data-testid="linkedin-btn">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" /></svg>
                    {t.role.linkedinLabel}
                  </a>
                )}
              </div>
            </div>
            <div className="card chain" data-testid="role-chain">
              {t.role.chain.map((c, i) => (
                <div key={i} className="chain-row">
                  <div className="num">{i + 1}</div>
                  <div>
                    <strong>{c.t}</strong>
                    <br />
                    <span className="p">{c.d}</span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="contact" className="bg-light">
        <div className="wrap contact">
          <Reveal className="contact-left">
            <p className="eyebrow">{t.booking.eyebrow}</p>
            <h2 className="h2">{t.booking.h2}</h2>
            <p className="lead">{t.booking.lead}</p>
            <p className="p" style={{ fontSize: "0.95rem" }}>{t.booking.note}</p>
          </Reveal>
          <Reveal className="contact-right" delay={0.1}>
            <Booking t={t.booking} />
          </Reveal>
        </div>
      </section>

      <section id="need">
        <div className="wrap sec">
          <div className="section-inner">
            <Reveal>
              <div className="section-head">
                <p className="eyebrow">{t.need.eyebrow}</p>
                <h2 className="h2">{t.need.h2}</h2>
                <p className="lead">{t.need.lead}</p>
              </div>
            </Reveal>
            <div className="grid-3" data-testid="doc-cards">
              {t.need.cards.map((c, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <DocCard title={c.t} items={c.items} labels={t.need} idx={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="bg-light">
        <div className="wrap sec">
          <div className="section-inner" style={{ gap: 24 }}>
            <div>
              <Reveal>
                <p className="eyebrow">{t.faq.eyebrow}</p>
                <h2 className="h2">{t.faq.h2}</h2>
              </Reveal>
            </div>
            <div data-testid="faq-list">
              {t.faq.items.map((f, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <details className={`faq${i === t.faq.items.length - 1 ? " faq-last" : ""}`} data-testid={`faq-item-${i + 1}`}>
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer t={t} />
    </div>
  );
}
